import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Filter, X, Search, RotateCcw, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MapFilterPanel = ({ filters, onChange, resultCount, onResetMap, onOpenChange }) => {
  const [open, setOpen] = useState(false);
  const { language } = useLanguage();
  const isArabic = language === 'ar';

  const [regions, setRegions] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [allDistricts, setAllDistricts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]);

  useEffect(() => {
    if (!API_URL) return;
    Promise.all([
      fetch(`${API_URL}/api/regions`).then(r => r.json()),
      fetch(`${API_URL}/api/cities`).then(r => r.json()),
      fetch(`${API_URL}/api/districts`).then(r => r.json()),
      fetch(`${API_URL}/api/categories`).then(r => r.json()),
      fetch(`${API_URL}/api/subcategories`).then(r => r.json()),
    ]).then(([reg, cit, dist, cat, sub]) => {
      setRegions(reg);
      setAllCities(cit);
      setAllDistricts(dist);
      setCategories(cat);
      setAllSubcategories(sub);
    }).catch(console.error);
  }, []);

  const toggleOpen = useCallback(() => {
    setOpen(v => {
      const next = !v;
      if (onOpenChange) onOpenChange(next);
      return next;
    });
  }, [onOpenChange]);

  const cityOptions = useMemo(() => {
    if (!filters.regionId) return allCities;
    return allCities.filter(c => c.region_id === filters.regionId);
  }, [allCities, filters.regionId]);

  const districtOptions = useMemo(() => {
    if (!filters.cityId) return allDistricts;
    return allDistricts.filter(d => d.city_id === filters.cityId);
  }, [allDistricts, filters.cityId]);

  const subcategoryOptions = useMemo(() => {
    if (!filters.categoryId) return allSubcategories;
    return allSubcategories.filter(s => s.category_id === filters.categoryId);
  }, [allSubcategories, filters.categoryId]);

  const set = useCallback((key, value) => {
    const next = { ...filters, [key]: value || '' };
    if (key === 'regionId') { next.cityId = ''; next.districtId = ''; }
    if (key === 'cityId') { next.districtId = ''; }
    if (key === 'categoryId') { next.subcategoryId = ''; }
    onChange(next);
  }, [filters, onChange]);

  const resetFilters = useCallback(() => {
    onChange({ regionId: '', cityId: '', districtId: '', categoryId: '', subcategoryId: '', search: '' });
  }, [onChange]);

  const hasFilters = filters.regionId || filters.cityId || filters.districtId
    || filters.categoryId || filters.subcategoryId || filters.search;

  const activeCount = [filters.regionId, filters.cityId, filters.districtId,
    filters.categoryId, filters.subcategoryId, filters.search].filter(Boolean).length;

  const t = isArabic
    ? { filter: 'تصفية', region: 'المنطقة', city: 'المدينة', district: 'الحي',
        category: 'الفئة', subcategory: 'الفئة الفرعية', search: 'بحث بالاسم...',
        reset: 'مسح الكل', resetMap: 'إعادة ضبط', results: 'نتيجة',
        all: 'الكل', location: 'الموقع', activity: 'النشاط' }
    : { filter: 'Filter', region: 'Region', city: 'City', district: 'District',
        category: 'Category', subcategory: 'Subcategory', search: 'Search by name...',
        reset: 'Clear All', resetMap: 'Reset Map', results: 'results',
        all: 'All', location: 'Location', activity: 'Activity' };

  return (
    <>
      {/* Toggle button */}
      <button
        data-testid="filter-toggle-btn"
        onClick={toggleOpen}
        className="mf-toggle"
      >
        {open ? <X size={18} /> : <SlidersHorizontal size={18} />}
        {hasFilters && !open && <span className="mf-toggle-badge">{activeCount}</span>}
      </button>

      {/* Panel */}
      <div
        data-testid="filter-panel"
        className={`mf-panel ${open ? 'mf-panel--open' : ''}`}
      >
        {/* Header */}
        <div className="mf-header">
          <div className="mf-header-title">
            <Filter size={15} className="mf-header-icon" />
            <span>{t.filter}</span>
          </div>
          <div className="mf-header-actions">
            {hasFilters && (
              <button className="mf-clear-btn" data-testid="reset-filters-btn" onClick={resetFilters}>
                {t.reset}
              </button>
            )}
            <button className="mf-close-btn" data-testid="filter-close-btn" onClick={toggleOpen}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mf-search" data-testid="filter-search-wrap">
          <Search size={14} className="mf-search-icon" />
          <input
            data-testid="filter-search-input"
            type="text"
            placeholder={t.search}
            value={filters.search}
            onChange={e => set('search', e.target.value)}
          />
        </div>

        {/* Scroll area */}
        <div className="mf-body">
          {/* Location group */}
          <div className="mf-group">
            <span className="mf-group-label">{t.location}</span>
            <MfSelect testId="filter-region" label={t.region} value={filters.regionId}
              options={regions} nameKey={isArabic ? 'name_ar' : 'name_en'} allLabel={t.all} onChange={v => set('regionId', v)} />
            <MfSelect testId="filter-city" label={t.city} value={filters.cityId}
              options={cityOptions} nameKey={isArabic ? 'name_ar' : 'name_en'} allLabel={t.all} onChange={v => set('cityId', v)} />
            <MfSelect testId="filter-district" label={t.district} value={filters.districtId}
              options={districtOptions} nameKey={isArabic ? 'name_ar' : 'name_en'} allLabel={t.all} onChange={v => set('districtId', v)} />
          </div>

          {/* Activity group */}
          <div className="mf-group">
            <span className="mf-group-label">{t.activity}</span>
            <MfSelect testId="filter-category" label={t.category} value={filters.categoryId}
              options={categories} nameKey={isArabic ? 'name_ar' : 'name_en'} allLabel={t.all} onChange={v => set('categoryId', v)} />
            <MfSelect testId="filter-subcategory" label={t.subcategory} value={filters.subcategoryId}
              options={subcategoryOptions} nameKey={isArabic ? 'name_ar' : 'name_en'} allLabel={t.all} onChange={v => set('subcategoryId', v)} />
          </div>
        </div>

        {/* Result count + Reset Map */}
        <div className="mf-footer">
          {hasFilters && (
            <span className="mf-result-count" data-testid="result-count-badge">{resultCount} {t.results}</span>
          )}
          <button data-testid="reset-map-btn" className="mf-reset-map-btn" onClick={onResetMap}>
            <RotateCcw size={13} /> {t.resetMap}
          </button>
        </div>
      </div>
    </>
  );
};

/* Compact select */
const MfSelect = ({ testId, label, value, options, nameKey, allLabel, onChange }) => (
  <div className="mf-field">
    <label className="mf-label">{label}</label>
    <div className="mf-select-wrap">
      <select data-testid={testId} className="mf-select" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">{allLabel}</option>
        {options.map(o => (
          <option key={o.id} value={o.id}>{o[nameKey] || o.name_ar}</option>
        ))}
      </select>
      <ChevronDown size={13} className="mf-select-arrow" />
    </div>
  </div>
);

export default MapFilterPanel;
